import { apiFetch } from "./client";
import {
  canonicalStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "./stocky_public_ids";

export type PurchaseOrderEmailReconciliationDecision = "sent" | "not_sent";

export type JsonValue =
  string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

export interface PurchaseOrderEmailReconciliationEvidence {
  source: string | null;
  observed_at: string | null;
  reference: string | null;
}

export interface PurchaseOrderEmailDelivery {
  id: StockyPublicBigIntId;
  supplier_id: StockyPublicBigIntId;
  location_id: StockyPublicBigIntId | null;
  requested_location_id: StockyPublicBigIntId | null;
  replayed_from_delivery_id: StockyPublicBigIntId | null;
  new_order_after_delivery_id: StockyPublicBigIntId | null;
  confirmation_token: "send_new_purchase_order" | null;
  status:
    "pending" | "in_flight" | "succeeded" | "failed" | "unknown" | "cancelled";
  idempotency_key: string;
  draft_sha: string;
  input_epoch: number;
  from_email: string;
  to_email: string;
  subject: string;
  text: string;
  purchase_order_note: string | null;
  accepted_at: string;
  idempotency_started_at: string | null;
  uncertain_at: string | null;
  delivered_at: string | null;
  has_last_error: boolean;
  reconciliation_decision: PurchaseOrderEmailReconciliationDecision | null;
  reconciled_at: string | null;
  reconciliation_note: string | null;
  reconciliation_evidence: PurchaseOrderEmailReconciliationEvidence | null;
}

export interface PurchaseOrderEmailReconcileInput {
  decision: PurchaseOrderEmailReconciliationDecision;
  confirmed: true;
  note: string;
  evidence: Record<string, JsonValue>;
}

interface PurchaseOrderEmailDeliveryList {
  deliveries: PurchaseOrderEmailDelivery[];
}

const PREFIX = "/api/purchase-order-email-deliveries/reconciliation";

export const purchaseOrderEmailReconciliationKeys = {
  all: ["purchase-order-email-deliveries"] as const,
  unresolved: ["purchase-order-email-deliveries", "unresolved"] as const,
  detail: (deliveryId: StockyPublicBigIntId) =>
    [
      "purchase-order-email-deliveries",
      "detail",
      canonicalStockyPublicBigIntId(deliveryId),
    ] as const,
};

function nullableId(value: unknown): string | null {
  return value == null ? null : canonicalStockyPublicBigIntId(value);
}

function nullableString(value: unknown): string | null {
  return value == null ? null : typeof value === "string" ? value : null;
}

function publicEvidence(
  value: unknown,
): PurchaseOrderEmailReconciliationEvidence | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const row = value as Record<string, unknown>;
  return {
    source: nullableString(row.source),
    observed_at: nullableString(row.observed_at),
    reference: nullableString(row.reference),
  };
}

/** SPA-first compatibility: project an old response to the new safe DTO. */
export function normalizePurchaseOrderEmailDelivery(
  value: unknown,
): PurchaseOrderEmailDelivery {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError("purchase-order email delivery response is invalid");
  }
  const row = value as Record<string, unknown>;
  const legacyPayload =
    row.provider_payload !== null &&
    typeof row.provider_payload === "object" &&
    !Array.isArray(row.provider_payload)
      ? (row.provider_payload as Record<string, unknown>)
      : null;
  const legacyContext =
    row.draft_context !== null &&
    typeof row.draft_context === "object" &&
    !Array.isArray(row.draft_context)
      ? (row.draft_context as Record<string, unknown>)
      : null;
  const legacyRecipients = legacyPayload?.to;
  const legacyNote = legacyContext?.supplier_po_note_template;
  const fromEmail = row.from_email ?? legacyPayload?.from;
  const toEmail =
    row.to_email ??
    (Array.isArray(legacyRecipients) && legacyRecipients.length === 1
      ? legacyRecipients[0]
      : null);
  const subject = row.subject ?? legacyPayload?.subject;
  const bodyText = row.text ?? legacyPayload?.text;
  const purchaseOrderNote =
    row.purchase_order_note ??
    (legacyNote !== null &&
    typeof legacyNote === "object" &&
    !Array.isArray(legacyNote)
      ? (legacyNote as Record<string, unknown>).text
      : null);
  if (
    typeof fromEmail !== "string" ||
    typeof toEmail !== "string" ||
    typeof subject !== "string" ||
    typeof bodyText !== "string" ||
    (purchaseOrderNote !== null &&
      purchaseOrderNote !== undefined &&
      typeof purchaseOrderNote !== "string") ||
    typeof row.status !== "string" ||
    typeof row.idempotency_key !== "string" ||
    typeof row.draft_sha !== "string" ||
    typeof row.input_epoch !== "number" ||
    typeof row.accepted_at !== "string"
  ) {
    throw new TypeError("purchase-order email delivery response is invalid");
  }
  return {
    id: canonicalStockyPublicBigIntId(row.id),
    supplier_id: canonicalStockyPublicBigIntId(row.supplier_id),
    location_id: nullableId(row.location_id),
    requested_location_id: nullableId(row.requested_location_id),
    replayed_from_delivery_id: nullableId(row.replayed_from_delivery_id),
    new_order_after_delivery_id: nullableId(row.new_order_after_delivery_id),
    confirmation_token:
      row.confirmation_token === "send_new_purchase_order"
        ? row.confirmation_token
        : null,
    status: row.status as PurchaseOrderEmailDelivery["status"],
    idempotency_key: row.idempotency_key,
    draft_sha: row.draft_sha,
    input_epoch: row.input_epoch,
    from_email: fromEmail,
    to_email: toEmail,
    subject,
    text: bodyText,
    purchase_order_note:
      typeof purchaseOrderNote === "string" ? purchaseOrderNote : null,
    accepted_at: row.accepted_at,
    idempotency_started_at: nullableString(row.idempotency_started_at),
    uncertain_at: nullableString(row.uncertain_at),
    delivered_at: nullableString(row.delivered_at),
    has_last_error:
      typeof row.has_last_error === "boolean"
        ? row.has_last_error
        : row.last_error != null,
    reconciliation_decision:
      row.reconciliation_decision === "sent" ||
      row.reconciliation_decision === "not_sent"
        ? row.reconciliation_decision
        : null,
    reconciled_at: nullableString(row.reconciled_at),
    reconciliation_note: nullableString(row.reconciliation_note),
    reconciliation_evidence: publicEvidence(row.reconciliation_evidence),
  };
}

export async function listPurchaseOrderEmailDeliveries(
  includeReconciled = false,
): Promise<PurchaseOrderEmailDeliveryList> {
  const result = await apiFetch<{ deliveries: unknown[] }>(PREFIX, {
    query: includeReconciled ? { include_reconciled: "true" } : undefined,
  });
  if (!Array.isArray(result.deliveries)) {
    throw new TypeError("purchase-order email delivery list is invalid");
  }
  return {
    deliveries: result.deliveries.map(normalizePurchaseOrderEmailDelivery),
  };
}

export async function getPurchaseOrderEmailDelivery(
  deliveryId: StockyPublicBigIntId,
): Promise<PurchaseOrderEmailDelivery> {
  const result = await apiFetch<unknown>(
    `${PREFIX}/${encodeURIComponent(canonicalStockyPublicBigIntId(deliveryId))}`,
  );
  return normalizePurchaseOrderEmailDelivery(result);
}

export async function reconcilePurchaseOrderEmailDelivery(
  deliveryId: StockyPublicBigIntId,
  input: PurchaseOrderEmailReconcileInput,
): Promise<PurchaseOrderEmailDelivery> {
  const result = await apiFetch<unknown>(
    `${PREFIX}/${encodeURIComponent(canonicalStockyPublicBigIntId(deliveryId))}/reconcile`,
    { method: "POST", body: input },
  );
  return normalizePurchaseOrderEmailDelivery(result);
}
